/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { reqStrategyService } from '../services';
import { useAuth } from './useAuth';
import type { ClonedArtifactIdentity, ReqStrategy } from '../types';

export const REQ_STRATEGIES_KEY = 'req-strategies';

/** A request strategy as an inheritance target: its type, and the key its own reads live under (`P3a`). */
export const REQ_STRATEGY_INHERITANCE = { type: 'req_strategy', queryRoot: REQ_STRATEGIES_KEY } as const;

export function useReqStrategies() {
  return useQuery({
    queryKey: [REQ_STRATEGIES_KEY],
    queryFn: () => reqStrategyService.list(),
  });
}

export function useReqStrategy(id: string) {
  return useQuery({
    queryKey: [REQ_STRATEGIES_KEY, id],
    queryFn: () => reqStrategyService.get(id),
    enabled: !!id,
  });
}

export function useCreateReqStrategy() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: (strategy: Partial<ReqStrategy>) => reqStrategyService.create(strategy, user?.email),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [REQ_STRATEGIES_KEY] }),
  });
}

/** The request strategy templates this viewer may see (`RQ10`, slice 7b), under the strategies' own key. */
export function useReqStrategyTemplates() {
  return useQuery({
    // An object segment, so no strategy id can share this key with `useReqStrategy(id)`.
    queryKey: [REQ_STRATEGIES_KEY, { kind: 'templates' }],
    queryFn: () => reqStrategyService.templates(),
  });
}

export function useUpdateReqStrategy() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: ({ id, strategy }: { id: string; strategy: Partial<ReqStrategy> }) =>
      reqStrategyService.update(id, strategy, user?.email),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [REQ_STRATEGIES_KEY] }),
  });
}

export function useDeleteReqStrategy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => reqStrategyService.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [REQ_STRATEGIES_KEY] }),
  });
}

export function useCloneReqStrategy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, copy }: { id: string; copy: ClonedArtifactIdentity }) => reqStrategyService.clone(id, copy),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [REQ_STRATEGIES_KEY] }),
  });
}

export function useSetReqStrategyEnabled() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      reqStrategyService.setEnabled(id, enabled, user?.email),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [REQ_STRATEGIES_KEY] }),
  });
}

export function useSetReqStrategyVisibility() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: ({ id, visibility }: { id: string; visibility: ReqStrategy['visibility'] }) =>
      reqStrategyService.setVisibility(id, visibility, user?.email),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [REQ_STRATEGIES_KEY] }),
  });
}
